/**
 * The three join steps (docs/06-UI-SPEC.md §4): class code, name, then the tap that
 * actually joins. Each one is a plain view — JoinPage owns the state and the server
 * calls, so the steps stay easy to read and to re-order.
 */

import { checkStudentName, NAME_MAX_LENGTH } from '@opal/shared';

import { CodeEntry } from './CodeEntry.js';
import { NumericKeypad } from './NumericKeypad.js';

const PRIMARY_BUTTON =
  'h-touch w-full max-w-[320px] rounded-md bg-primary px-6 text-base font-medium text-text-inverse transition duration-fast ease-out hover:bg-primary-hover active:bg-primary-active disabled:cursor-not-allowed disabled:opacity-40';

export interface CodeStepProps {
  readonly code: string;
  readonly onCodeChange: (code: string) => void;
  readonly onContinue: () => void;
  readonly error: string | null;
  readonly checking: boolean;
  readonly useKeypad: boolean;
}

export function CodeStep({
  code,
  onCodeChange,
  onContinue,
  error,
  checking,
  useKeypad,
}: CodeStepProps) {
  return (
    <>
      <h1 className="text-xl font-semibold text-text-primary">Enter your class code</h1>
      <CodeEntry
        value={code}
        onChange={onCodeChange}
        invalid={error !== null}
        useKeypad={useKeypad}
      />
      {error === null ? null : <p className="text-base text-danger">{error}</p>}
      {useKeypad ? (
        <NumericKeypad
          onDigit={(digit) => {
            onCodeChange(`${code}${digit}`.slice(0, 6));
          }}
          onBackspace={() => {
            onCodeChange(code.slice(0, -1));
          }}
        />
      ) : null}
      <button
        type="button"
        className={PRIMARY_BUTTON}
        disabled={code.length < 6 || checking}
        onClick={onContinue}
      >
        {checking ? 'Checking…' : 'Continue'}
      </button>
    </>
  );
}

export interface NameStepProps {
  readonly name: string;
  readonly onNameChange: (name: string) => void;
  readonly onContinue: () => void;
}

export function NameStep({ name, onNameChange, onContinue }: NameStepProps) {
  const check = checkStudentName(name);
  const showProblem = name.trim() !== '' && !check.ok;

  return (
    <>
      <h1 className="text-xl font-semibold text-text-primary">What is your name?</h1>
      <input
        type="text"
        autoFocus
        value={name}
        maxLength={NAME_MAX_LENGTH}
        placeholder="Type your first name"
        onChange={(event) => {
          onNameChange(event.target.value);
        }}
        className="h-touch w-full max-w-[320px] rounded-md border border-border-strong bg-surface px-3 text-center text-lg text-text-primary placeholder:text-text-placeholder"
      />
      {showProblem ? <p className="text-base text-danger">{check.message}</p> : null}
      <button type="button" className={PRIMARY_BUTTON} disabled={!check.ok} onClick={onContinue}>
        Continue
      </button>
    </>
  );
}

export interface ConfirmStepProps {
  readonly code: string;
  readonly name: string;
  readonly onJoin: () => void;
  readonly joining: boolean;
  readonly error: string | null;
}

export function ConfirmStep({ code, name, onJoin, joining, error }: ConfirmStepProps) {
  return (
    <>
      <h1 className="text-xl font-semibold text-text-primary">Ready to join!</h1>
      <dl className="flex flex-col gap-2 text-base">
        <div className="flex gap-3">
          <dt className="text-text-secondary">Class code:</dt>
          <dd className="font-semibold tabular text-text-primary">{code}</dd>
        </div>
        <div className="flex gap-3">
          <dt className="text-text-secondary">Your name:</dt>
          <dd className="font-semibold text-text-primary">{name}</dd>
        </div>
      </dl>
      {error === null ? null : <p className="text-base text-danger">{error}</p>}
      {/* SRS B3.1 — this tap is what unlocks sound on the student's device. */}
      <button type="button" className={PRIMARY_BUTTON} disabled={joining} onClick={onJoin}>
        {joining ? 'Joining…' : 'Tap to join'}
      </button>
    </>
  );
}
